import React from 'react';

/**
 * Base atomic Skeleton block with shimmer animation
 */
export function Skeleton({
  width = '100%',
  height = '16px',
  borderRadius = '8px',
  circle = false,
  style = {},
  className = ''
}) {
  return (
    <div
      className={`skeleton-shimmer ${className}`}
      style={{
        width,
        height,
        borderRadius: circle ? '50%' : borderRadius,
        flexShrink: 0,
        ...style
      }}
    />
  );
}

/**
 * Metric / Stats Card Skeleton Row
 * Fits HROverview, ManagerDashboard, and BUDashboard stats grids
 */
export function StatsGridSkeleton({ count = 4, style = {} }) {
  return (
    <div style={{ ...styles.statsGrid, ...style }}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={styles.statCard}>
          <Skeleton width="48px" height="48px" borderRadius="14px" />
          <div style={styles.statInfo}>
            <Skeleton width="60%" height="13px" borderRadius="4px" style={{ marginBottom: '8px' }} />
            <Skeleton width="40%" height="24px" borderRadius="6px" />
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Candidate Screening Card Skeleton
 * Matches the CandidateScreening layout: score wheel + candidate details + criteria breakdown
 */
export function CandidateCardSkeleton({ count = 3 }) {
  return (
    <div style={styles.candidatesList}>
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} style={styles.candidateCard}>
          {/* Match Score Wheel Placeholder */}
          <div style={styles.scoreSection}>
            <Skeleton width="72px" height="72px" circle style={{ marginBottom: '8px' }} />
            <Skeleton width="45px" height="11px" borderRadius="4px" />
          </div>

          {/* Details Panel Placeholder */}
          <div style={styles.candidateInfo}>
            <div style={styles.candHeaderRow}>
              <div>
                <Skeleton width="180px" height="20px" borderRadius="6px" style={{ marginBottom: '8px' }} />
                <Skeleton width="130px" height="13px" borderRadius="4px" />
              </div>
              <Skeleton width="160px" height="14px" borderRadius="4px" />
            </div>

            {/* Criteria Breakdown Grid */}
            <div style={styles.breakdownGrid}>
              {Array.from({ length: 4 }).map((_, j) => (
                <div key={j} style={styles.breakdownPill}>
                  <Skeleton width="75px" height="11px" borderRadius="4px" style={{ marginBottom: '6px' }} />
                  <Skeleton width="100%" height="8px" borderRadius="4px" />
                </div>
              ))}
            </div>

            {/* Bottom Tag & Actions Row */}
            <div style={styles.candFooterRow}>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Skeleton width="65px" height="22px" borderRadius="12px" />
                <Skeleton width="75px" height="22px" borderRadius="12px" />
                <Skeleton width="55px" height="22px" borderRadius="12px" />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Skeleton width="90px" height="32px" borderRadius="8px" />
                <Skeleton width="100px" height="32px" borderRadius="8px" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Hiring Pipeline Kanban Board Skeleton
 * Replicates the 4-stage Kanban columns with candidate card placeholders
 */
export function PipelineBoardSkeleton({ columns = 4 }) {
  return (
    <div style={styles.pipelineBoard}>
      {Array.from({ length: columns }).map((_, colIdx) => (
        <div key={colIdx} style={styles.pipelineColumn}>
          {/* Column Header */}
          <div style={styles.columnHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Skeleton width="10px" height="10px" circle />
              <Skeleton width="110px" height="16px" borderRadius="4px" />
            </div>
            <Skeleton width="28px" height="20px" borderRadius="10px" />
          </div>

          {/* Cards inside column */}
          <div style={styles.columnCards}>
            {Array.from({ length: colIdx === 0 ? 3 : colIdx === 3 ? 1 : 2 }).map((_, cardIdx) => (
              <div key={cardIdx} style={styles.pipelineCard}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                  <Skeleton width="75px" height="18px" borderRadius="10px" />
                  <Skeleton width="55px" height="18px" borderRadius="10px" />
                </div>
                <Skeleton width="130px" height="16px" borderRadius="4px" style={{ marginBottom: '6px' }} />
                <Skeleton width="100px" height="12px" borderRadius="4px" style={{ marginBottom: '12px' }} />
                
                {/* Skills tags */}
                <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
                  <Skeleton width="45px" height="18px" borderRadius="6px" />
                  <Skeleton width="50px" height="18px" borderRadius="6px" />
                </div>

                {/* Footer Buttons */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid var(--border-light, #f1f5f9)' }}>
                  <Skeleton width="28px" height="24px" borderRadius="6px" />
                  <Skeleton width="70px" height="24px" borderRadius="6px" />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * Tabular View Skeleton
 * Used for employee rosters, requisitions lists, exit requests
 */
export function TableSkeleton({ rows = 5, columns = 5, style = {} }) {
  return (
    <div style={{ ...styles.tableCard, ...style }}>
      {/* Header Row */}
      <div style={styles.tableHeaderRow}>
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} width={`${60 + (i % 3) * 15}%`} height="14px" borderRadius="4px" />
        ))}
      </div>

      {/* Table Data Rows */}
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} style={styles.tableDataRow}>
          {Array.from({ length: columns }).map((_, c) => (
            <Skeleton
              key={c}
              width={c === 0 ? '75%' : c === columns - 1 ? '45%' : '60%'}
              height="14px"
              borderRadius="4px"
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/**
 * Full Dashboard Overview Skeleton (Header + Stats Grid + Card Grid)
 */
export function DashboardOverviewSkeleton({ statsCount = 4, showGrid = true }) {
  return (
    <div style={styles.overviewWrapper}>
      {/* Page Title & Subtitle Header */}
      <div style={{ marginBottom: '24px' }}>
        <Skeleton width="240px" height="28px" borderRadius="8px" style={{ marginBottom: '10px' }} />
        <Skeleton width="420px" height="15px" borderRadius="4px" />
      </div>

      {/* Stats Cards Row */}
      <StatsGridSkeleton count={statsCount} style={{ marginBottom: '28px' }} />

      {/* Content Section Placeholder */}
      {showGrid ? (
        <div style={styles.dashboardTwoCol}>
          <div style={styles.contentBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <Skeleton width="160px" height="18px" borderRadius="6px" />
              <Skeleton width="70px" height="18px" borderRadius="6px" />
            </div>
            <Skeleton width="100%" height="160px" borderRadius="12px" style={{ marginBottom: '14px' }} />
            <Skeleton width="85%" height="14px" borderRadius="4px" />
          </div>

          <div style={styles.contentBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
              <Skeleton width="180px" height="18px" borderRadius="6px" />
              <Skeleton width="80px" height="28px" borderRadius="8px" />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <Skeleton width="100%" height="42px" borderRadius="8px" />
              <Skeleton width="100%" height="42px" borderRadius="8px" />
              <Skeleton width="100%" height="42px" borderRadius="8px" />
            </div>
          </div>
        </div>
      ) : (
        <TableSkeleton rows={5} columns={5} />
      )}
    </div>
  );
}

export default Skeleton;

const styles = {
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '28px',
    width: '100%',
    boxSizing: 'border-box'
  },
  statCard: {
    background: 'var(--bg-card, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: 'var(--shadow-card, 0 2px 4px rgba(0,0,0,0.02))',
    boxSizing: 'border-box'
  },
  statInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column'
  },
  candidatesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    width: '100%'
  },
  candidateCard: {
    background: 'var(--bg-card, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    gap: '24px',
    alignItems: 'flex-start',
    boxShadow: 'var(--shadow-card, 0 4px 6px -1px rgba(0,0,0,0.04))',
    boxSizing: 'border-box'
  },
  scoreSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '80px',
    paddingTop: '4px'
  },
  candidateInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '14px'
  },
  candHeaderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '12px'
  },
  breakdownGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
    gap: '10px',
    padding: '12px',
    background: 'var(--bg-surface, #f8fafc)',
    borderRadius: '12px',
    border: '1px solid var(--border-light, #edf2f7)'
  },
  breakdownPill: {
    display: 'flex',
    flexDirection: 'column'
  },
  candFooterRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
    paddingTop: '8px'
  },
  pipelineBoard: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
    width: '100%',
    alignItems: 'flex-start'
  },
  pipelineColumn: {
    background: 'var(--bg-surface, #f8fafc)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '14px',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    minHeight: '400px'
  },
  columnHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingBottom: '12px',
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  columnCards: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  pipelineCard: {
    background: 'var(--bg-card, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '12px',
    padding: '14px',
    boxShadow: 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.05))'
  },
  tableCard: {
    background: 'var(--bg-card, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: 'var(--shadow-card, 0 2px 4px rgba(0,0,0,0.02))'
  },
  tableHeaderRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '16px',
    padding: '16px 20px',
    background: 'var(--bg-surface, #f8fafc)',
    borderBottom: '1px solid var(--border-color, #e2e8f0)'
  },
  tableDataRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, 1fr)',
    gap: '16px',
    padding: '16px 20px',
    borderBottom: '1px solid var(--divider-color, rgba(0,0,0,0.05))',
    alignItems: 'center'
  },
  overviewWrapper: {
    width: '100%',
    boxSizing: 'border-box'
  },
  dashboardTwoCol: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
    gap: '20px'
  },
  contentBox: {
    background: 'var(--bg-card, #ffffff)',
    border: '1px solid var(--border-color, #e2e8f0)',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: 'var(--shadow-card, 0 2px 4px rgba(0,0,0,0.02))'
  }
};
